/**
 * FCM web push (#101, mirrors seller-dashboard #78) — token lifecycle +
 * foreground messages. Configured via REACT_APP_FIREBASE_* env vars (baked
 * at build time). When they're absent (e.g. local dev without a Firebase
 * project) every entry point is a logged no-op; the bell then only updates
 * via REST on mount/open/refocus (#100: push is the sole realtime channel).
 */
import { registerDeviceToken, unregisterDeviceToken } from '../api/deviceTokens';

const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
	appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const TOKEN_STORAGE_KEY = 'pushDeviceToken';

export const isPushConfigured = () =>
	!!(
		firebaseConfig.apiKey &&
		firebaseConfig.projectId &&
		firebaseConfig.messagingSenderId &&
		firebaseConfig.appId &&
		VAPID_KEY
	);

let messagingPromise = null;

/**
 * Lazily initialize Firebase messaging. Resolves null when push is
 * unconfigured or unsupported by the browser.
 */
const getMessaging = () => {
	if (typeof window === 'undefined' || !isPushConfigured()) {
		return Promise.resolve(null);
	}
	if (!messagingPromise) {
		messagingPromise = (async () => {
			try {
				const { isSupported, getMessaging: get } = await import('firebase/messaging');
				if (!(await isSupported())) {
					console.info('Push disabled: browser does not support FCM web push');
					return null;
				}
				const { initializeApp, getApps } = await import('firebase/app');
				const app = getApps()[0] || initializeApp(firebaseConfig);
				return get(app);
			} catch (err) {
				console.warn('Push disabled: Firebase init failed', err);
				return null;
			}
		})();
	}
	return messagingPromise;
};

/**
 * The service worker gets the Firebase config via query params so nothing
 * is hardcoded in public/firebase-messaging-sw.js.
 */
const registerServiceWorker = async () => {
	const params = new URLSearchParams(firebaseConfig).toString();
	return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params}`);
};

let registerInFlight = null;

/**
 * Ask permission, fetch the FCM token and register it with the backend.
 * Safe to call on every session start — registration is a server-side
 * upsert, and concurrent calls share one flight (parallel first-time
 * getToken calls race in the FCM SDK and mint different tokens).
 */
export const registerWebPushToken = () => {
	if (!registerInFlight) {
		registerInFlight = doRegisterWebPushToken().finally(() => {
			registerInFlight = null;
		});
	}
	return registerInFlight;
};

/**
 * Register on session start without a quiet-UI prompt (#100): Chrome demotes
 * permission prompts that don't follow a user gesture to its easy-to-miss
 * bell UI. Permission granted → register immediately; undecided → register
 * on the first click/keypress (a real gesture); denied → skip.
 * Returns a cleanup that removes the pending gesture listeners.
 */
export const armWebPushRegistration = () => {
	if (typeof window === 'undefined' || typeof Notification === 'undefined'
		|| !isPushConfigured()) {
		return () => {};
	}
	if (Notification.permission === 'granted') {
		registerWebPushToken();
		return () => {};
	}
	if (Notification.permission === 'denied') {
		return () => {};
	}

	const onGesture = () => {
		cleanup();
		registerWebPushToken();
	};
	const cleanup = () => {
		window.removeEventListener('pointerdown', onGesture);
		window.removeEventListener('keydown', onGesture);
	};
	window.addEventListener('pointerdown', onGesture);
	window.addEventListener('keydown', onGesture);
	return cleanup;
};

const doRegisterWebPushToken = async () => {
	const messaging = await getMessaging();
	if (!messaging) return;

	try {
		const { getToken } = await import('firebase/messaging');
		const serviceWorkerRegistration = await registerServiceWorker();
		const token = await getToken(messaging, {
			vapidKey: VAPID_KEY,
			serviceWorkerRegistration,
		});
		if (!token) return;

		await registerDeviceToken({ token, platform: 'web' });
		localStorage.setItem(TOKEN_STORAGE_KEY, token);
	} catch (err) {
		// Permission denied lands here too — the bell still works via REST.
		console.warn('Web push registration failed', err);
	}
};

/**
 * Delete this browser's token server-side and drop it locally.
 * Must run BEFORE logout clears auth — the API call needs the session.
 */
export const unregisterWebPushToken = async () => {
	try {
		const token = localStorage.getItem(TOKEN_STORAGE_KEY);
		if (token) {
			await unregisterDeviceToken({ token });
			localStorage.removeItem(TOKEN_STORAGE_KEY);
		}

		const messaging = await getMessaging();
		if (messaging) {
			const { deleteToken } = await import('firebase/messaging');
			await deleteToken(messaging);
		}
	} catch (err) {
		console.warn('Web push unregister failed', err);
	}
};

/**
 * Invalidate this browser's FCM token WITHOUT the server call — for the 401
 * auto-logout path where the session is already dead so the authenticated
 * unregister endpoint is unreachable. deleteToken() kills the token at FCM,
 * so pushes stop being delivered and the backend prunes the dead row on its
 * next send.
 */
export const deleteLocalPushToken = async () => {
	try {
		localStorage.removeItem(TOKEN_STORAGE_KEY);
		const messaging = await getMessaging();
		if (messaging) {
			const { deleteToken } = await import('firebase/messaging');
			await deleteToken(messaging);
		}
	} catch (err) {
		console.warn('Local push token cleanup failed', err);
	}
};

// The SDK's onMessage holds a single handler, so arm it once and fan out —
// per-caller onMessage subscriptions overwrite each other when the bell is
// mounted more than once (desktop + mobile nav).
const foregroundSubscribers = new Set();
let dispatcherInFlight = null;

const armDispatcher = () => {
	if (!dispatcherInFlight) {
		dispatcherInFlight = (async () => {
			const messaging = await getMessaging();
			if (!messaging) return;
			const { onMessage } = await import('firebase/messaging');
			onMessage(messaging, (payload) => {
				foregroundSubscribers.forEach((cb) => cb(payload));
			});
		})();
	}
	return dispatcherInFlight;
};

/**
 * Foreground messages (the SDK shows no browser notification while a tab
 * is focused). Returns a cleanup function; no messages ever arrive when
 * push is unconfigured.
 */
export const armForegroundMessages = async (callback) => {
	foregroundSubscribers.add(callback);
	await armDispatcher();
	return () => foregroundSubscribers.delete(callback);
};
